import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { subscriptionAPI, historyAPI, formatCurrency, formatDate, getStatusColor, getStatusLabel, getOfferTypeLabel } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const SubscriptionDetail = () => {
  const { id } = useParams();

  // Récupérer les détails de la souscription
  const { data: subscriptionData, isLoading: subscriptionLoading, error, isError } = useQuery(
    ['subscription', id],
    () => subscriptionAPI.getSubscription(id),
    {
      enabled: !!id,
      staleTime: 0, // Force le rechargement
      cacheTime: 0  // Pas de cache
    }
  );

  // Extraire la souscription de la réponse API
  let subscription = subscriptionData?.data?.subscription || 
                    subscriptionData?.subscription || 
                    subscriptionData?.data;

  // Si l'objet contient {success: true, data: {...}}, extraire les vraies données
  if (subscription && subscription.success && subscription.data) {
    subscription = subscription.data.subscription || subscription.data;
  }

  // Récupérer l'historique de la souscription depuis la table histories
  const { data: historyData, isLoading: historyLoading } = useQuery(
    ['subscription-history', id],
    () => historyAPI.getEntityHistory('subscriptions', id),
    {
      enabled: !!id,
      staleTime: 0,
      cacheTime: 0
    }
  );

  const history = historyData?.data?.data || historyData?.data || [];
  const isLoading = subscriptionLoading || historyLoading;

  // Debug temporaire pour vérifier les données reçues
  console.log('🔍 DEBUG DONNÉES UTILISATEUR:', history.map(entry => ({
    description: entry.description,
    createdBy_User: entry.createdBy_User,
    mutualiste: entry.createdBy_User?.mutualiste
  })));

  // Fonction pour formater la date avec l'heure
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  // Calculer la date de début selon le type de produit
  const getStartDate = () => {
    // Pour FPM : 1er jour du mois suivant la souscription
    if (subscription.offer?.type === 'HEALTH_COVERAGE' || subscription.offer?.type === 'HEALTH') {
      const subscriptionDate = new Date(subscription.createdAt);
      const nextMonth = new Date(subscriptionDate.getFullYear(), subscriptionDate.getMonth() + 1, 1);
      return nextMonth.toISOString();
    }
    
    // Pour les autres produits, utiliser la date renseignée par le mutualiste (metadata)
    if (subscription.metadata?.startDate) {
      return subscription.metadata.startDate;
    }
    
    // Si startDate existe dans la base, l'utiliser
    if (subscription.startDate) {
      return subscription.startDate;
    }
    
    return null;
  };

  // Récupérer le taux d'intérêt depuis les features du produit pour les prêts
  const getLoanInterestRate = () => {
    if (subscription.offer?.type === 'LOAN' && subscription.offer?.features) {
      // Chercher le taux dans les features
      const interestRateFeature = subscription.offer.features.find(feature => 
        feature.toLowerCase().includes('taux') || 
        feature.toLowerCase().includes('intérêt') ||
        feature.toLowerCase().includes('%')
      );
      
      if (interestRateFeature) {
        // Extraire le pourcentage de la feature
        const match = interestRateFeature.match(/(\d+(?:\.\d+)?)\s*%/);
        if (match) {
          return parseFloat(match[1]);
        }
      }
    }
    
    // Valeur par défaut si pas trouvé dans les features
    return subscription.metadata?.interestRate ? 
           (subscription.metadata.interestRate * 100) : 
           12; // 12% par défaut
  };

  // Calculer le montant de l'échéance pour les prêts
  const calculateInstallment = () => {
    if (subscription.offer?.type === 'LOAN' && subscription.metadata?.duration && subscription.amount) {
      const principal = parseFloat(subscription.amount);
      const duration = subscription.metadata.duration;
      const annualRate = getLoanInterestRate() / 100; // Convertir le pourcentage en décimal
      
      // Formule simple : (Montant du prêt * Taux d'intérêt) / Durée de remboursement
      const totalInterest = principal * annualRate;
      const installment = (principal + totalInterest) / duration;
      
      return installment;
    }
    return null;
  };

  const getOfferIcon = (type) => {
    switch (type) {
      case 'HEALTH_COVERAGE':
        return '🏥';
      case 'SAVINGS':
        return '💰';
      case 'LOAN':
        return '💳';
      default:
        return '📄';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">Erreur: {error?.response?.data?.error || error?.message}</p>
        <Link to="/subscriptions" className="btn-primary">
          Retour aux souscriptions
        </Link>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Souscription non trouvée</p>
        <Link to="/subscriptions" className="btn-primary">
          Retour aux souscriptions
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Navigation */}
      <div className="flex items-center space-x-4">
        <Link
          to="/subscriptions"
          className="flex items-center text-primary-600 hover:text-primary-700"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Retour aux souscriptions
        </Link>
      </div>

      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center text-3xl">
              {getOfferIcon(subscription.offer?.type)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary-900">
                {subscription.offer?.name}
              </h1>
              <p className="text-gray-600">
                {getOfferTypeLabel(subscription.offer?.type)}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <span className={`badge badge-${getStatusColor(subscription.status?.libelle || subscription.status)}`}>
                  {getStatusLabel(subscription.status?.libelle || subscription.status)}
                </span>
                <span className="text-sm text-gray-500">
                  Souscrit le {formatDate(subscription.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Détails de la souscription */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations générales */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <h2 className="text-xl font-semibold text-primary-900 mb-6">
            Informations générales
          </h2>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Montant</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(subscription.amount)}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Date de souscription</span>
              <span className="text-gray-900">
                {formatDate(subscription.createdAt)}
              </span>
            </div>
            
            {/* Date de début calculée */}
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-gray-600">Date de début</span>
              <span className="text-gray-900">
                {getStartDate() ? formatDate(getStartDate()) : 'À définir'}
              </span>
            </div>
            
            {/* Durée et échéance pour les prêts */}
            {subscription.offer?.type === 'LOAN' && subscription.metadata?.duration && (
              <>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Durée du remboursement</span>
                  <span className="text-gray-900">
                    {subscription.metadata.duration} mois
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-600">Taux d'intérêt annuel</span>
                  <span className="font-semibold text-gray-900">
                    {getLoanInterestRate().toFixed(2)}%
                  </span>
                </div>
                
                {calculateInstallment() && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Montant de l'échéance</span>
                    <span className="font-semibold text-gray-900">
                      {formatCurrency(calculateInstallment())}
                    </span>
                  </div>
                )}
              </>
            )}
            
            {/* Taux d'intérêt pour l'épargne */}
            {subscription.offer?.type === 'SAVINGS' && subscription.metadata?.interestRate && (
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Taux d'intérêt annuel</span>
                <span className="font-semibold text-gray-900">
                  {(subscription.metadata.interestRate * 100).toFixed(2)}%
                </span>
              </div>
            )}
            
            {subscription.endDate && (
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Date de fin</span>
                <span className="text-gray-900">
                  {formatDate(subscription.endDate)}
                </span>
              </div>
            )}
            
            <div className="flex justify-between items-center py-3">
              <span className="text-gray-600">Statut</span>
              <span className={`badge badge-${getStatusColor(subscription.status?.libelle || subscription.status)}`}>
                {getStatusLabel(subscription.status?.libelle || subscription.status)}
              </span>
            </div>
          </div>
        </div>

        {/* Historique */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <h2 className="text-xl font-semibold text-primary-900 mb-6">
            Historique
          </h2>
          
          <div className="space-y-4">
            {history.length > 0 ? (
              history.map((entry, index) => {
                const getHistoryColor = (action) => {
                  switch (action) {
                    case 'CREATED': return 'bg-primary-500';
                    case 'APPROVED': return 'bg-green-500';
                    case 'REJECTED': return 'bg-red-500';
                    case 'CANCELLED': return 'bg-gray-500';
                    default: return 'bg-blue-500';
                  }
                };
                
                const getHistoryBg = (action) => {
                  switch (action) {
                    case 'CREATED': return 'bg-gray-50';
                    case 'APPROVED': return 'bg-green-50';
                    case 'REJECTED': return 'bg-red-50';
                    case 'CANCELLED': return 'bg-gray-50';
                    default: return 'bg-blue-50';
                  }
                };
                
                return (
                  <div key={entry.id} className={`flex items-start space-x-3 p-4 rounded-lg ${getHistoryBg(entry.metadata?.action)}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getHistoryColor(entry.metadata?.action)}`} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {entry.description}
                      </p>
                      {(entry.metadata?.reason || entry.metadata?.cancellationReason) && (
                        <p className="text-sm text-gray-600 mt-1">
                          Raison : {entry.metadata.reason || entry.metadata.cancellationReason}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-400">
                          {formatDateTime(entry.createdAt)}
                        </p>
                        {entry.createdBy_User ? (
                          <p className="text-xs text-gray-500">
                            Par {entry.createdBy_User.mutualiste ? 
                              `${entry.createdBy_User.mutualiste.prenoms} ${entry.createdBy_User.mutualiste.nom}` : 
                              entry.createdBy_User.email
                            }
                            {entry.createdBy_User.role === 'ADMIN' && (
                              <span className="ml-1 text-blue-600 font-medium">(Admin)</span>
                            )}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500">
                            Par le système
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              // Fallback si pas d'historique dans la base
              <div className="text-center py-8 text-gray-500">
                <p>Aucun historique disponible pour cette souscription.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetail;
